import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { Button, Tabs, Input, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import {
  useSuggestions,
  type SuggestionWithStats,
} from '../hooks/useSuggestions'
import SuggestionFormModal from '../components/SuggestionFormModal'
import SuggestionReviewModal from '../components/SuggestionReviewModal'
import SuggestionCard from '../components/SuggestionCard'
import { Search, Plus } from 'lucide-react'
import { AlertModal } from '@/shared/ui/AlertModal'

export default function SugestoesPage() {
  const { user, profile } = useAuth()
  const { add: toast } = useToast()
  const {
    items,
    loading,
    error,
    create,
    update,
    review,
    vote,
    remove,
    role,
    userId,
  } = useSuggestions()

  const isMaster =
    !!user?.email &&
    (isMasterByEmail(user.email) || profile?.role === 'master')

  const [activeTab, setActiveTab] = React.useState<string>(
    isMaster ? 'published' : 'all',
  )
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] =
    React.useState<'all' | 'approved' | 'in_voting'>('all')

  const [createOpen, setCreateOpen] = React.useState(false)
  const [createSaving, setCreateSaving] = React.useState(false)

  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] =
    React.useState<SuggestionWithStats | null>(null)
  const [editSaving, setEditSaving] = React.useState(false)

  const [reviewOpen, setReviewOpen] = React.useState(false)
  const [reviewAction, setReviewAction] =
    React.useState<'approve' | 'reject'>('approve')
  const [reviewTargetId, setReviewTargetId] =
    React.useState<string | null>(null)
  const [reviewSaving, setReviewSaving] = React.useState(false)

  const [voteFeedback, setVoteFeedback] = React.useState<{
    open: boolean
    direction: 'up' | 'down' | 'clear' | null
  }>({ open: false, direction: null })
  const [confirmDelete, setConfirmDelete] = React.useState<{
    open: boolean
    id?: string
  }>({ open: false })

  const normalizedSearch = search.trim().toLowerCase()

  const published = items.filter(
    (it) => it.status === 'published' || it.status === 'approved',
  )
  const pending = items.filter((it) => it.status === 'pending')
  const approved = items.filter((it) => it.status === 'approved')
  const mine = userId ? items.filter((it) => it.author_id === userId) : []

  const _legacyFilteredPublished = published.filter((it) => {
    if (
      normalizedSearch &&
      !it.title.toLowerCase().includes(normalizedSearch)
    ) {
      return false
    }

    const hasTarget = it.targetMin > 0
    const finishedApproved = hasTarget && it.votesUp >= it.targetMin

    if (statusFilter === 'approved') {
      // Considera aprovadas quando a votação atingiu o alvo
      // ou quando o status já está marcado como approved
      return finishedApproved || it.status === 'approved'
    }

    if (statusFilter === 'in_voting') {
      // Em votação: apenas publicadas que ainda não atingiram o alvo mínimo
      // e não estão marcadas como rejected
      if (it.status === 'rejected') return false
      if (finishedApproved || it.status === 'approved') return false
      return it.status === 'published'
    }

    return true
  })

  const filteredPublished = published.filter((it) => {
    if (
      normalizedSearch &&
      !it.title.toLowerCase().includes(normalizedSearch)
    ) {
      return false
    }

    const hasTarget = it.targetMin > 0
    const reachedTarget = hasTarget && it.votesUp >= it.targetMin
    const communityApproved = reachedTarget || it.status === 'approved'
    const communityRejected = it.status === 'rejected'
    const isVotingClosed = communityApproved || communityRejected

    if (statusFilter === 'approved') {
      return communityApproved
    }

    if (statusFilter === 'in_voting') {
      if (isVotingClosed) return false
      return it.status === 'published'
    }

    return true
  })

  void _legacyFilteredPublished

  async function handleCreate(values: { title: string; content: string }) {
    try {
      setCreateSaving(true)
      await create(values.title, values.content)
      setCreateOpen(false)
      toast({
        title: 'Sugestão enviada',
        description:
          'Sua sugestão foi enviada para análise da equipe do Cripto Alert. Se aprovada, será listada para votação da comunidade.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Falha ao salvar',
        description: e?.message || 'Tente novamente em instantes.',
        variant: 'error',
        soft: true,
      })
    } finally {
      setCreateSaving(false)
    }
  }

  async function handleEdit(values: { title: string; content: string; thresholdPct?: number }) {
    if (!editing) return
    try {
      setEditSaving(true)
      await update(editing.id, {
        title: values.title,
        content: values.content,
        thresholdPct: values.thresholdPct,
      })
      setEditOpen(false)
      setEditing(null)
      toast({
        title: 'Sugestão atualizada',
        description: 'A sugestão foi atualizada com sucesso.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Falha ao salvar',
        description: e?.message || 'Tente novamente.',
        variant: 'error',
        soft: true,
      })
    } finally {
      setEditSaving(false)
    }
  }

  async function handleReviewConfirm(opts: {
    note: string
    visibility: 'author' | 'public'
    authorPrivateNote?: string
  }) {
    if (!reviewTargetId) return
    try {
      setReviewSaving(true)
      await review(reviewTargetId, reviewAction, {
        note: opts.note,
        visibility: opts.visibility,
        authorPrivateNote: opts.authorPrivateNote ?? null,
      })
      setReviewOpen(false)
      setReviewTargetId(null)
      toast({
        title:
          reviewAction === 'approve'
            ? 'Sugestão aprovada'
            : 'Sugestão negada',
        description:
          reviewAction === 'approve'
            ? 'A sugestão foi aprovada e, se publicada, ficará aberta para votação da comunidade.'
            : 'A sugestão foi marcada como negada.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Falha ao registrar parecer',
        description: e?.message || 'Tente novamente.',
        variant: 'error',
        soft: true,
      })
    } finally {
      setReviewSaving(false)
    }
  }

  async function handleVote(
    id: string,
    direction: 'up' | 'down' | 'clear',
  ) {
    try {
      await vote(id, direction)
      setVoteFeedback({ open: true, direction })
    } catch (e: any) {
      toast({
        title: 'Falha ao registrar voto',
        description: e?.message || 'Tente novamente em instantes.',
        variant: 'error',
        soft: true,
      })
    }
  }

  function renderList(list: SuggestionWithStats[], mineView: boolean) {
    if (list.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-border/60 p-6 text-sm text-foreground/70">
          Nenhuma sugestão encontrada.
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {list.map((item) => (
          <SuggestionCard
            key={item.id}
            item={item}
            isMine={mineView && !!userId && item.author_id === userId}
            isAuthor={!!userId && item.author_id === userId}
            canModerate={isMaster}
            onVote={handleVote}
            onRequestEdit={
              isMaster
                ? (s) => {
                    setEditing(s)
                    setEditOpen(true)
                  }
                : undefined
            }
            onRequestReview={
              isMaster
                ? (id, action) => {
                    setReviewTargetId(id)
                    setReviewAction(action)
                    setReviewOpen(true)
                  }
                : undefined
            }
            onRequestDelete={
              isMaster
                ? (id) => setConfirmDelete({ open: true, id })
                : undefined
            }
          />
        ))}
      </div>
    )
  }

  const tabsMaster = [
    { value: 'published', label: 'Publicadas e em votação' },
    { value: 'pending', label: 'Em análise' },
    { value: 'approved', label: 'Aprovadas' },
  ]

  const tabsMember = [
    { value: 'all', label: 'Sugestões em votação' },
    { value: 'mine', label: 'Minhas sugestões' },
  ]

  const voteModalTitle =
    voteFeedback.direction === 'clear' ? 'Voto removido' : 'Votação computada'

  const voteModalDescription =
    voteFeedback.direction === 'up'
      ? 'Seu voto a favor desta sugestão foi registrado com sucesso.'
      : voteFeedback.direction === 'down'
      ? 'Seu voto contra esta sugestão foi registrado com sucesso.'
      : voteFeedback.direction === 'clear'
      ? 'O seu voto foi removido com sucesso.'
      : 'Seu voto foi registrado com sucesso.'

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold">Sugestões</h1>
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="bg-lime-400 text-black hover:bg-lime-500"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova sugestão
          </Button>
        </div>
        <div className="text-sm text-foreground/70">
          Envie ideias de melhorias para o Cripto Alert e acompanhe o apoio da
          comunidade. Apenas sugestões aprovadas pelo time serão abertas para
          votação.
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <Tabs
          tabs={isMaster ? tabsMaster : tabsMember}
          value={activeTab}
          onValueChange={setActiveTab}
        />

        {(!isMaster && activeTab === 'all') ||
        (isMaster && activeTab === 'published') ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar pelo título da sugestão"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-foreground/70">Status:</span>
              <Button
                type="button"
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('all')}
                className="h-7 px-3 text-xs"
              >
                Todas
              </Button>
              <Button
                type="button"
                variant={statusFilter === 'in_voting' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('in_voting')}
                className="h-7 px-3 text-xs"
              >
                Em votação
              </Button>
              <Button
                type="button"
                variant={statusFilter === 'approved' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('approved')}
                className="h-7 px-3 text-xs"
              >
                Apenas aprovadas
              </Button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-md border border-border/60 p-4 text-sm text-foreground/70">
            Carregando sugestões...
          </div>
        ) : isMaster ? (
          <>
            {activeTab === 'published' && renderList(filteredPublished, false)}
            {activeTab === 'pending' && renderList(pending, false)}
            {activeTab === 'approved' && renderList(approved, false)}
          </>
        ) : (
          <>
            {activeTab === 'all' && renderList(filteredPublished, false)}
            {activeTab === 'mine' && (
              <div className="space-y-3">
                <div className="text-xs text-foreground/70">
                  Aqui você vê apenas as suas sugestões e o status de cada uma
                  (em análise, aprovada, negada).
                </div>
                {renderList(mine, true)}
              </div>
            )}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-foreground/60">
        <Badge variant="soft" color="info">
          Regra de aprovação
        </Badge>
        <span>
          Uma sugestão é considerada aprovada pela comunidade quando atinge o
          alvo mínimo de votos favoráveis definido pela equipe.
        </span>
      </div>

      <SuggestionFormModal
        open={createOpen}
        mode="create"
        saving={createSaving}
        onClose={() => {
          if (!createSaving) setCreateOpen(false)
        }}
        onSubmit={handleCreate}
      />

      <SuggestionFormModal
        open={editOpen}
        mode="edit"
        initial={
          editing
            ? {
                title: editing.title,
                content: editing.content,
                thresholdPct: editing.approval_threshold_pct ?? 51,
              }
            : undefined
        }
        enableThreshold={isMaster}
        saving={editSaving}
        onClose={() => {
          if (!editSaving) {
            setEditOpen(false)
            setEditing(null)
          }
        }}
        onSubmit={handleEdit}
      />

      <SuggestionReviewModal
        open={reviewOpen}
        action={reviewAction}
        saving={reviewSaving}
        onClose={() => {
          if (!reviewSaving) {
            setReviewOpen(false)
            setReviewTargetId(null)
          }
        }}
        onConfirm={handleReviewConfirm}
      />

      <AlertModal
        open={voteFeedback.open}
        onClose={() => setVoteFeedback({ open: false, direction: null })}
        variant="success"
        title={voteModalTitle}
        description={voteModalDescription}
        confirmText="Fechar"
      />

      <AlertModal
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, id: undefined })}
        variant="warning"
        title="Excluir sugestão"
        description="Tem certeza que deseja excluir esta sugestão? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        onConfirm={async () => {
          const id = confirmDelete.id
          setConfirmDelete({ open: false, id: undefined })
          if (!id) return
          try {
            await remove(id)
            toast({
              title: 'Sugestão excluída',
              description: 'A sugestão foi removida da lista.',
              variant: 'success',
              soft: true,
            })
          } catch (e: any) {
            toast({
              title: 'Falha ao excluir sugestão',
              description: e?.message || 'Tente novamente.',
              variant: 'error',
              soft: true,
            })
          }
        }}
      />
    </div>
  )
}
